import { useState, type FormEvent } from 'react'
import { Link } from 'react-router'

import { ApiError, api, forgetCsrfToken } from '../api.js'
import { Alert, Card, Field } from '../components/ui.js'
import { Wordmark } from '../components/wordmark.js'

/** The masthead on both auth cards, so signup and sign-in read as one place. */
export function Masthead({ caption }: { caption: string }) {
  return (
    <div className="card__brand">
      <Wordmark className="wordmark--md" />
      <span>
        <span className="rail__brand-name">Propene</span>
        <span className="rail__brand-sub">{caption}</span>
      </span>
    </div>
  )
}

export function Register({ onSignedIn }: { onSignedIn: () => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    setBusy(true)
    setError(null)

    try {
      await api.register({
        email: String(form.get('email') ?? ''),
        password: String(form.get('password') ?? ''),
        businessName: String(form.get('businessName') ?? ''),
      })
      // Registration regenerated the session id, so the cached CSRF token is
      // now stale. Forgetting it here is what stops the next action 403-ing.
      forgetCsrfToken()
      // Tell the shell rather than navigating: the router only knows the
      // signed-out routes until the shell flips, so a navigate('/') here
      // would bounce straight back to sign-in.
      onSignedIn()
    } catch (err) {
      setError(messageFor(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="centered">
      <Card>
        <Masthead caption="Create your business account" />

        {error ? <Alert>{error}</Alert> : null}

        <form onSubmit={onSubmit}>
          <Field label="Business name">
            <input name="businessName" required maxLength={200} autoComplete="organization" placeholder="Masiga Stores" />
          </Field>

          <Field label="Email">
            <input name="email" type="email" required autoComplete="email" placeholder="you@example.com" />
          </Field>

          <Field label="Password" hint="At least 10 characters. A phrase you can remember beats a short scramble.">
            <input name="password" type="password" required minLength={10} autoComplete="new-password" />
          </Field>

          <div className="actions">
            <button type="submit" className="primary wide" disabled={busy}>
              {busy ? 'Creating account…' : 'Create account'}
            </button>
          </div>
        </form>

        <p className="card__note" style={{ margin: '1rem 0 0' }}>
          Already registered? <Link to="/sign-in">Sign in</Link>
        </p>
      </Card>
    </div>
  )
}

export function messageFor(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Something went wrong. Please try again.'
  switch (err.code) {
    case 'email_taken':
      return 'That email is already registered. Try signing in instead.'
    case 'weak_password':
      return err.message
    case 'invalid_input':
      return 'Please check the details above.'
    case 'invalid_credentials':
      return 'That email and password do not match.'
    case 'too_many_attempts':
      return 'Too many attempts. Please wait a few minutes and try again.'
    case 'payments_unavailable':
      return 'Payments are not available right now. Please try again shortly.'
    case 'payment_provider_error':
      return 'The payment provider refused that request. Please try again.'
    case 'device_not_found':
      return 'That device is no longer set up.'
    case 'subscription_not_paid':
      return err.message
    default:
      return 'Something went wrong. Please try again.'
  }
}
